import { useModelsContext } from "@/providers/models-provider/models-provider";
import { useEffect, useState } from "react";
import { VoiceModel } from "@/actions/db/get-models";
import $filters, {
  Filter,
  ownFilterCategoryName,
  publicFilterCategoryName,
} from "@/data/filters.constants";
import { useDebouncedState } from "@/hooks/use-debounced-state";
import { useCurrentUser } from "@/hooks/use-current-user";

const isMatchingSearchFilter = (modelName: string, searchValue: string) => {
  return modelName.toLowerCase().includes(searchValue.toLowerCase());
};

const filterData = (
  userId: string | undefined,
  models: VoiceModel[] | undefined,
  searchValue: string,
  selectedFilter: string,
  selectedGender?: Filter,
) => {
  if (!models) return [];

  let filteredData = [...models];

  if (selectedFilter !== "all" && selectedFilter !== "") {
    if (selectedFilter === "favourites") {
      filteredData = filteredData.filter((voice) => voice.isFavourite);
    } else if (selectedFilter === ownFilterCategoryName) {
      filteredData = filteredData.filter((voice) => voice.ownerId === userId);
    } else if (selectedFilter === publicFilterCategoryName) {
      filteredData = filteredData.filter(
        (voice) =>
          voice.genre?.toLowerCase() === ownFilterCategoryName &&
          voice.ownerId !== userId,
      );
    } else {
      filteredData = filteredData.filter((voice) => {
        //ця умова лишеється бо ще не всі категорії для голосів перенесенні в
        //нову таблицю, цей кусок доцільно буде прибрати вже коли всі категорії
        //для голосів будуть точно перенсенні в таблицю VoiceModelCategories
        if (!voice.categories || voice.categories.length == 0) {
          return voice.genre?.toLowerCase() === selectedFilter;
        }

        return (
          voice.categories.findIndex((p) => p.categoryName == selectedFilter) !=
          -1
        );
      });
    }
  }

  if (selectedGender) {
    filteredData = filteredData.filter(
      (voice) => voice.gender === selectedGender.category,
    );
  }

  if (searchValue.trim() !== "") {
    filteredData = filteredData.filter((voice) => {
      const namesToTest = [
        voice.name,
        ...voice.ModelAlternateNames.map((altName) => altName.name),
      ];

      return namesToTest.some((name) =>
        isMatchingSearchFilter(name, searchValue),
      );
    });
  }

  return filteredData;
};

const useModelsData = () => {
  const user = useCurrentUser();
  const { isLoading, models } = useModelsContext();
  const [searchValue, setSearchValue] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedGender, setSelectedGender] = useState<Filter>();
  const [selectedFilter, setSelectedFilter] = useState<Filter>(
    $filters.defaultModelFilter,
  );

  const [filteredVoiceList, setFilteredVoiceList] = useState(models);

  const { debouncedValue: debouncedSearchValue } =
    useDebouncedState(searchValue);

  useEffect(() => {
    if (debouncedSearchValue) {
      setHasSearched(true);
    }
    if (debouncedSearchValue === "") setHasSearched(false);
  }, [debouncedSearchValue]);

  useEffect(() => {
    setFilteredVoiceList(
      filterData(
        user?.id,
        models,
        debouncedSearchValue,
        selectedFilter.category,
        selectedGender,
      ),
    );
  }, [
    models,
    user,
    debouncedSearchValue,
    selectedFilter.category,
    selectedGender,
  ]);

  const handleGenderSelect = (gender?: Filter) => {
    setSelectedGender(gender);
  };

  const handleFilterSelect = (selectedFilter: Filter) => {
    setSelectedFilter(selectedFilter);
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
  };

  return {
    isLoading,
    searchValue: debouncedSearchValue,
    hasSearched,
    filteredVoiceList,
    handleFilterSelect,
    handleGenderSelect,
    handleSearch,
  };
};

export default useModelsData;
